import { canCancelOrder, OrderStatus } from '@models';
import { useStore } from '@stores';
import { Button, Popconfirm } from 'antd';
import { observer } from 'mobx-react';
import { footerButtonStyle } from './footerButtonStyle';

/**
 * "Cancel job" for the job's footer. A cancelled job is as good as deleted - it is only kept, with
 * this status, for the statistics - so there is no reopening it. Saves straight away: cancelling
 * is not a field to stage for Save.
 */
export const CancelJobButton = observer(({ block }: { block?: boolean }) => {
  const { orderStore } = useStore();
  const { order } = orderStore;

  if (!order || !canCancelOrder(order, orderStore.savedStatus ?? order.status)) return null;

  // Only the status goes to the server, not the whole card: unsaved edits to the services, dates or
  // times are not committed by cancelling, and a failed call leaves the job exactly as it was.
  const cancelJob = async (restoreInventory: boolean) => {
    const result = await orderStore.updateStatus({
      id: order.id,
      status: OrderStatus.Canceled,
      // The API reads a missing mileage as 0, so it is sent as it stands.
      mileage: order.mileage,
      restoreInventory,
    } as any);
    if (!result) return;
    orderStore.updateOrdersStatus(order.id, OrderStatus.Canceled);
    orderStore.applySavedOrderToCalendar(result);
  };

  // The popconfirm's two buttons are the two ways to cancel - parts back in stock or left where
  // they are; dismissing it (click away, Esc) is the way back.
  return (
    <Popconfirm
      title="Cancel this job?"
      description="Should the inventory parts used on it be returned to stock? A cancelled job cannot be reopened."
      okText="Return parts and cancel"
      cancelText="Keep parts and cancel"
      okButtonProps={{ danger: true }}
      onConfirm={() => cancelJob(true)}
      onCancel={() => cancelJob(false)}
      disabled={orderStore.loading || orderStore.statusLoading}
    >
      <Button type="primary" danger style={footerButtonStyle(block)} disabled={orderStore.loading}>
        Cancel job
      </Button>
    </Popconfirm>
  );
});
